import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";

/**
 * ExecutiveHero — White institutional cinematic hero.
 *
 * Renders a large editorial image as a soft right-side panel with the
 * institutional typography on the left. Designed for /, /about, /wait-theory,
 * /programs, /scfr-journal, /juswork, /zodiac-lms-lab, /observatory, /partnership.
 *
 * Props:
 *   eyebrow       — small uppercase tracking-widest label above the title
 *   title         — H1 string or node (renders with Playfair Display)
 *   accentTitle   — optional second line styled as a gold-italic emphasis
 *   subtitle      — supporting paragraph
 *   image         — URL to cinematic 4K image (used as right-side panel)
 *   actions       — array of {label, href, variant?: 'primary' | 'ghost'}
 *   breadcrumb    — array of {label, href?} for top breadcrumb trail
 */
export default function ExecutiveHero({
  eyebrow,
  title,
  accentTitle,
  subtitle,
  image,
  actions = [],
  breadcrumb = [],
  testId = "executive-hero",
}) {
  return (
    <section
      className="relative overflow-hidden bg-white"
      data-testid={testId}
    >
      {/* Ambient texture */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-[#1E3A8A]/[0.04] blur-3xl" />
        <div className="absolute -bottom-40 right-1/3 w-[420px] h-[420px] rounded-full bg-[#C8A157]/[0.05] blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-20 pb-16 lg:pb-24">
        {breadcrumb.length > 0 && (
          <nav aria-label="Breadcrumb" className="mb-6 flex items-center gap-1 text-sm text-[#6B7280]">
            {breadcrumb.map((b, i) => (
              <span key={i} className="flex items-center gap-1">
                {b.href ? (
                  <Link to={b.href} className="hover:text-[#1E3A8A] transition-colors">{b.label}</Link>
                ) : (
                  <span className="text-[#0F1E47] font-medium">{b.label}</span>
                )}
                {i < breadcrumb.length - 1 && <ChevronRight className="w-3.5 h-3.5 text-[#9CA3AF]" />}
              </span>
            ))}
          </nav>
        )}

        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
          >
            {eyebrow && (
              <div className="zodiac-eyebrow mb-4 flex items-center gap-3">
                <span className="w-8 h-px bg-[#1E3A8A]" />
                {eyebrow}
              </div>
            )}

            <h1 className="zodiac-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
              {title}
              {accentTitle && (
                <>
                  <br />
                  <span className="italic text-[#991B1B] font-normal">{accentTitle}</span>
                </>
              )}
            </h1>

            <div className="zodiac-divider-gold my-6" />

            {subtitle && (
              <p className="text-base sm:text-lg text-[#475569] leading-relaxed max-w-xl">
                {subtitle}
              </p>
            )}

            {actions.length > 0 && (
              <div className="mt-8 flex flex-wrap gap-3">
                {actions.map((a, i) => (
                  <Link key={i} to={a.href} data-testid={a.testId}>
                    <button
                      className={
                        a.variant === "ghost"
                          ? "h-12 px-6 rounded-md border border-[#0F1E47]/15 text-[#0F1E47] hover:bg-[#0F1E47]/[0.04] font-medium transition-colors"
                          : "h-12 px-6 rounded-md bg-[#1E3A8A] text-white hover:bg-[#15307A] font-semibold transition-colors shadow-[0_4px_14px_rgba(30,58,138,0.25)]"
                      }
                    >
                      <span className="inline-flex items-center gap-2">
                        {a.icon && <a.icon className="w-4 h-4" />}
                        {a.label}
                      </span>
                    </button>
                  </Link>
                ))}
              </div>
            )}
          </motion.div>

          {image && (
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="relative"
            >
              <div className="relative aspect-[5/4] sm:aspect-[4/3] lg:aspect-[5/4] rounded-2xl overflow-hidden shadow-[0_24px_80px_rgba(15,30,71,0.18)] ring-1 ring-[#0F1E47]/5">
                <img
                  src={image}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="eager"
                />
                {/* Subtle inner vignette */}
                <div className="absolute inset-0 bg-gradient-to-tr from-[#0F1E47]/10 via-transparent to-transparent" />
              </div>
              {/* Decorative corner card */}
              <div className="absolute -bottom-5 -left-5 hidden sm:block bg-white rounded-xl shadow-[0_8px_30px_rgba(15,30,71,0.12)] px-4 py-3 border border-[#E5E7EB]">
                <div className="text-[10px] uppercase tracking-widest text-[#1E3A8A] font-semibold">
                  Workforce Intelligence
                </div>
                <div className="text-sm text-[#0F1E47] font-medium">Human · AI · Institution</div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
